/**
 * Pricing Engine Domain Seed Data
 *
 * Contains all seed data for Pricing Engine Service (33):
 * - Pricing Rules (calculation rules)
 * - Pricing Lookups (key-value tables)
 * - Pricing Matrices (multi-dimensional tables)
 * - Sample Pricing Quotes
 *
 * @see 33-pricing-engine-service/prisma/schema.prisma
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// PRICING RULE IDs
// =============================================================================

export const PRICING_RULE_IDS = {
  // Interest rate rules
  BASE_INTEREST_RATE: seedId('pricing-rule-base-rate', 1),
  LTV_RATE_ADJUSTMENT: seedId('pricing-rule-ltv-adj', 2),
  CREDIT_SCORE_ADJUSTMENT: seedId('pricing-rule-credit-adj', 3),
  PROPERTY_TYPE_ADJUSTMENT: seedId('pricing-rule-prop-adj', 4),

  // Fee rules
  ORIGINATION_FEE: seedId('pricing-rule-orig-fee', 5),
  PROCESSING_FEE: seedId('pricing-rule-proc-fee', 6),
  APPRAISAL_FEE: seedId('pricing-rule-appraisal-fee', 7),
  UNDERWRITING_FEE: seedId('pricing-rule-uw-fee', 8),

  // Commission rules
  BROKER_COMMISSION: seedId('pricing-rule-broker-comm', 9),
  REFERRAL_BONUS: seedId('pricing-rule-referral-bonus', 10),

  // Issue #470: Mortgage pricing rules (for canary scenarios)
  MORTGAGE_BASE_RATE: seedId('pricing-rule-mortgage-base', 11),
  MORTGAGE_LTV_ADJUSTMENT: seedId('pricing-rule-mortgage-ltv', 12),
  MORTGAGE_CREDIT_ADJUSTMENT: seedId('pricing-rule-mortgage-credit', 13),
  MORTGAGE_ORIGINATION_FEE: seedId('pricing-rule-mortgage-orig', 14),
  MORTGAGE_PROCESSING_FEE: seedId('pricing-rule-mortgage-proc', 15),

  // Issue #526: Investment-type-specific pricing rules
  // Bridge/Fix & Flip specific rules
  BRIDGE_BASE_RATE: seedId('pricing-rule-bridge-base', 16),
  BRIDGE_REHAB_BUDGET_FEE: seedId('pricing-rule-bridge-rehab', 17),
  BRIDGE_EXIT_STRATEGY_ADJ: seedId('pricing-rule-bridge-exit', 18),
  BRIDGE_EXTENSION_FEE: seedId('pricing-rule-bridge-ext', 19),

  // Construction specific rules
  CONSTRUCTION_BASE_RATE: seedId('pricing-rule-const-base', 20),
  CONSTRUCTION_DRAW_FEE: seedId('pricing-rule-const-draw', 21),
  CONSTRUCTION_INSPECTION_FEE: seedId('pricing-rule-const-inspect', 22),
  CONSTRUCTION_CONTINGENCY_RESERVE: seedId('pricing-rule-const-contingency', 23),

  // Rental/DSCR specific rules
  DSCR_BASE_RATE: seedId('pricing-rule-dscr-base', 24),
  DSCR_RATIO_ADJUSTMENT: seedId('pricing-rule-dscr-ratio', 25),
  DSCR_PREPAY_PENALTY: seedId('pricing-rule-dscr-prepay', 26),
  DSCR_CASH_OUT_ADJUSTMENT: seedId('pricing-rule-dscr-cashout', 27),

  // Commercial specific rules
  COMMERCIAL_BASE_RATE: seedId('pricing-rule-comm-base', 28),
  COMMERCIAL_TENANT_QUALITY_ADJ: seedId('pricing-rule-comm-tenant', 29),
  COMMERCIAL_ENVIRONMENTAL_FEE: seedId('pricing-rule-comm-env', 30),
  COMMERCIAL_LEASE_REVIEW_FEE: seedId('pricing-rule-comm-lease', 31),
} as const;

export type PricingRuleId = (typeof PRICING_RULE_IDS)[keyof typeof PRICING_RULE_IDS];

// =============================================================================
// PRICING LOOKUP IDs
// =============================================================================

export const PRICING_LOOKUP_IDS = {
  STATE_FEES: seedId('pricing-lookup-state-fees', 1),
  PROPERTY_TYPE_MULTIPLIERS: seedId('pricing-lookup-prop-mult', 2),
  LOAN_PRODUCT_BASE_RATES: seedId('pricing-lookup-product-rates', 3),
  DOCUMENT_FEES: seedId('pricing-lookup-doc-fees', 4),
} as const;

export type PricingLookupId = (typeof PRICING_LOOKUP_IDS)[keyof typeof PRICING_LOOKUP_IDS];

// =============================================================================
// PRICING MATRIX IDs
// =============================================================================

export const PRICING_MATRIX_IDS = {
  INTEREST_RATE_MATRIX: seedId('pricing-matrix-rate', 1),
  ORIGINATION_FEE_MATRIX: seedId('pricing-matrix-orig', 2),
  RISK_ADJUSTMENT_MATRIX: seedId('pricing-matrix-risk', 3),
} as const;

export type PricingMatrixId = (typeof PRICING_MATRIX_IDS)[keyof typeof PRICING_MATRIX_IDS];

// =============================================================================
// PRICING QUOTE IDs (Sample quotes)
// =============================================================================

export const PRICING_QUOTE_IDS = {
  QUOTE_MARCUS_BRIDGE: seedId('pricing-quote-marcus', 1),
  QUOTE_JENNIFER_FIXFLIP: seedId('pricing-quote-jennifer', 2),
  QUOTE_COMMERCIAL_DSCR: seedId('pricing-quote-commercial', 3),
} as const;

export type PricingQuoteId = (typeof PRICING_QUOTE_IDS)[keyof typeof PRICING_QUOTE_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export type CalculationType = 'FLAT' | 'PERCENTAGE' | 'TIERED' | 'FORMULA' | 'LOOKUP' | 'MATRIX';

export interface PricingRuleSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  category?: string;
  entityType: string;
  conditions?: Record<string, unknown>;
  calculationType: CalculationType;
  calculationConfig: Record<string, unknown>;
  outputField: string;
  outputLabel: string;
  priority: number;
  effectiveDate: string;
  expirationDate?: string;
  isActive: boolean;
  createdBy: string;
}

export interface PricingLookupSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  lookupData: Record<string, number | string>;
  version: number;
  isActive: boolean;
  createdBy: string;
}

export interface PricingMatrixSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  dimensions: Array<{
    name: string;
    type: 'range' | 'discrete';
    ranges?: Array<{ min: number; max: number; label: string }>;
    values?: string[];
  }>;
  matrixData: Record<string, number>;
  version: number;
  isActive: boolean;
  createdBy: string;
}

export interface PricingQuoteSeedData {
  id: string;
  businessId: string;
  entityType: string;
  entityId?: string;
  inputData: Record<string, unknown>;
  totalAmount: number;
  currency: string;
  status: 'DRAFT' | 'LOCKED' | 'EXPIRED' | 'ACCEPTED' | 'REJECTED';
  expiresAt?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  createdBy: string;
}

// =============================================================================
// PRICING RULE DATA
// =============================================================================

export const PRICING_RULES: Record<keyof typeof PRICING_RULE_IDS, PricingRuleSeedData> = {
  BASE_INTEREST_RATE: {
    id: PRICING_RULE_IDS.BASE_INTEREST_RATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'base_interest_rate',
    description: 'Base interest rate determined by loan product type',
    category: 'interest_rate',
    entityType: 'loan',
    calculationType: 'LOOKUP',
    calculationConfig: {
      lookupId: PRICING_LOOKUP_IDS.LOAN_PRODUCT_BASE_RATES,
      lookupKey: 'loan_type',
      defaultValue: 0.12,
    },
    outputField: 'base_rate',
    outputLabel: 'Base Interest Rate',
    priority: 1,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  LTV_RATE_ADJUSTMENT: {
    id: PRICING_RULE_IDS.LTV_RATE_ADJUSTMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'ltv_rate_adjustment',
    description: 'Interest rate adjustment based on Loan-to-Value ratio',
    category: 'interest_rate',
    entityType: 'loan',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'ltv_ratio',
      tiers: [
        { min: 0, max: 0.6, adjustment: -0.005 },
        { min: 0.6, max: 0.7, adjustment: 0 },
        { min: 0.7, max: 0.75, adjustment: 0.005 },
        { min: 0.75, max: 0.8, adjustment: 0.01 },
        { min: 0.8, max: 1, adjustment: 0.015 },
      ],
      method: 'marginal',
    },
    outputField: 'ltv_adjustment',
    outputLabel: 'LTV Rate Adjustment',
    priority: 2,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  CREDIT_SCORE_ADJUSTMENT: {
    id: PRICING_RULE_IDS.CREDIT_SCORE_ADJUSTMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'credit_score_adjustment',
    description: 'Interest rate adjustment based on borrower credit score',
    category: 'interest_rate',
    entityType: 'loan',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'credit_score',
      tiers: [
        { min: 760, max: 850, adjustment: -0.0075 },
        { min: 720, max: 759, adjustment: -0.005 },
        { min: 680, max: 719, adjustment: 0 },
        { min: 640, max: 679, adjustment: 0.01 },
        { min: 600, max: 639, adjustment: 0.02 },
        { min: 0, max: 599, adjustment: 0.03 },
      ],
      method: 'flat',
    },
    outputField: 'credit_adjustment',
    outputLabel: 'Credit Score Adjustment',
    priority: 3,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  PROPERTY_TYPE_ADJUSTMENT: {
    id: PRICING_RULE_IDS.PROPERTY_TYPE_ADJUSTMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'property_type_adjustment',
    description: 'Interest rate adjustment based on property type',
    category: 'interest_rate',
    entityType: 'loan',
    calculationType: 'LOOKUP',
    calculationConfig: {
      lookupId: PRICING_LOOKUP_IDS.PROPERTY_TYPE_MULTIPLIERS,
      lookupKey: 'property_type',
      defaultValue: 0,
    },
    outputField: 'property_adjustment',
    outputLabel: 'Property Type Adjustment',
    priority: 4,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  ORIGINATION_FEE: {
    id: PRICING_RULE_IDS.ORIGINATION_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'origination_fee',
    description: 'Loan origination fee as percentage of loan amount',
    category: 'fee',
    entityType: 'loan',
    calculationType: 'PERCENTAGE',
    calculationConfig: {
      baseField: 'loan_amount',
      percentage: 0.02, // 2%
      minimum: 5000,
      maximum: 50000,
    },
    outputField: 'origination_fee',
    outputLabel: 'Origination Fee',
    priority: 10,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  PROCESSING_FEE: {
    id: PRICING_RULE_IDS.PROCESSING_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'processing_fee',
    description: 'Fixed loan processing fee',
    category: 'fee',
    entityType: 'loan',
    calculationType: 'FLAT',
    calculationConfig: {
      amount: 995,
    },
    outputField: 'processing_fee',
    outputLabel: 'Processing Fee',
    priority: 11,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  APPRAISAL_FEE: {
    id: PRICING_RULE_IDS.APPRAISAL_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'appraisal_fee',
    description: 'Property appraisal fee based on property type and value',
    category: 'fee',
    entityType: 'loan',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'property_value',
      tiers: [
        { min: 0, max: 500000, amount: 450 },
        { min: 500000, max: 1000000, amount: 650 },
        { min: 1000000, max: 2500000, amount: 850 },
        { min: 2500000, max: 5000000, amount: 1250 },
        { min: 5000000, max: Infinity, amount: 2000 },
      ],
      method: 'flat',
    },
    outputField: 'appraisal_fee',
    outputLabel: 'Appraisal Fee',
    priority: 12,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  UNDERWRITING_FEE: {
    id: PRICING_RULE_IDS.UNDERWRITING_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'underwriting_fee',
    description: 'Underwriting fee based on loan complexity',
    category: 'fee',
    entityType: 'loan',
    calculationType: 'FORMULA',
    calculationConfig: {
      formula: 'base + (loan_amount / 100000) * increment',
      variables: {
        base: 750,
        increment: 50,
      },
      minimum: 750,
      maximum: 3500,
    },
    outputField: 'underwriting_fee',
    outputLabel: 'Underwriting Fee',
    priority: 13,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  BROKER_COMMISSION: {
    id: PRICING_RULE_IDS.BROKER_COMMISSION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'broker_commission',
    description: 'Broker commission based on loan amount',
    category: 'commission',
    entityType: 'loan',
    conditions: { has_broker: true },
    calculationType: 'PERCENTAGE',
    calculationConfig: {
      baseField: 'loan_amount',
      percentage: 0.01, // 1%
      minimum: 2500,
      maximum: 25000,
    },
    outputField: 'broker_commission',
    outputLabel: 'Broker Commission',
    priority: 20,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  REFERRAL_BONUS: {
    id: PRICING_RULE_IDS.REFERRAL_BONUS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'referral_bonus',
    description: 'Referral partner bonus',
    category: 'commission',
    entityType: 'loan',
    conditions: { referral_source: { $exists: true } },
    calculationType: 'FLAT',
    calculationConfig: {
      amount: 500,
    },
    outputField: 'referral_bonus',
    outputLabel: 'Referral Bonus',
    priority: 21,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  // Issue #470: Mortgage pricing rules (for canary scenarios)
  MORTGAGE_BASE_RATE: {
    id: PRICING_RULE_IDS.MORTGAGE_BASE_RATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'mortgage_base_interest_rate',
    description: 'Base interest rate for mortgage products',
    category: 'interest_rate',
    entityType: 'mortgage',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'term_years',
      tiers: [
        { min: 0, max: 15, value: 0.0625 },
        { min: 15, max: 20, value: 0.0675 },
        { min: 20, max: 30, value: 0.0725 },
        { min: 30, max: 40, value: 0.0775 },
      ],
      method: 'flat',
    },
    outputField: 'base_rate',
    outputLabel: 'Base Interest Rate',
    priority: 1,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  MORTGAGE_LTV_ADJUSTMENT: {
    id: PRICING_RULE_IDS.MORTGAGE_LTV_ADJUSTMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'mortgage_ltv_rate_adjustment',
    description: 'Mortgage interest rate adjustment based on LTV ratio',
    category: 'interest_rate',
    entityType: 'mortgage',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'ltv_ratio',
      tiers: [
        { min: 0, max: 0.6, adjustment: -0.0025 },
        { min: 0.6, max: 0.7, adjustment: 0 },
        { min: 0.7, max: 0.8, adjustment: 0.0025 },
        { min: 0.8, max: 0.9, adjustment: 0.005 },
        { min: 0.9, max: 0.95, adjustment: 0.0075 },
        { min: 0.95, max: 1, adjustment: 0.01 },
      ],
      method: 'flat',
    },
    outputField: 'ltv_adjustment',
    outputLabel: 'LTV Rate Adjustment',
    priority: 2,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  MORTGAGE_CREDIT_ADJUSTMENT: {
    id: PRICING_RULE_IDS.MORTGAGE_CREDIT_ADJUSTMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'mortgage_credit_score_adjustment',
    description: 'Mortgage interest rate adjustment based on credit score',
    category: 'interest_rate',
    entityType: 'mortgage',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'credit_score',
      tiers: [
        { min: 760, max: 850, adjustment: -0.005 },
        { min: 720, max: 759, adjustment: -0.0025 },
        { min: 680, max: 719, adjustment: 0 },
        { min: 640, max: 679, adjustment: 0.005 },
        { min: 600, max: 639, adjustment: 0.0125 },
        { min: 0, max: 599, adjustment: 0.025 },
      ],
      method: 'flat',
    },
    outputField: 'credit_adjustment',
    outputLabel: 'Credit Score Adjustment',
    priority: 3,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  MORTGAGE_ORIGINATION_FEE: {
    id: PRICING_RULE_IDS.MORTGAGE_ORIGINATION_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'mortgage_origination_fee',
    description: 'Mortgage origination fee as percentage of loan amount',
    category: 'fee',
    entityType: 'mortgage',
    calculationType: 'PERCENTAGE',
    calculationConfig: {
      baseField: 'loan_amount',
      percentage: 0.01, // 1%
      minimum: 1000,
      maximum: 10000,
    },
    outputField: 'origination_fee',
    outputLabel: 'Origination Fee',
    priority: 10,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  MORTGAGE_PROCESSING_FEE: {
    id: PRICING_RULE_IDS.MORTGAGE_PROCESSING_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'mortgage_processing_fee',
    description: 'Fixed mortgage processing fee',
    category: 'fee',
    entityType: 'mortgage',
    calculationType: 'FLAT',
    calculationConfig: {
      amount: 500,
    },
    outputField: 'processing_fee',
    outputLabel: 'Processing Fee',
    priority: 11,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  // ==========================================================================
  // BRIDGE/FIX & FLIP INVESTMENT TYPE RULES (Issue #526)
  // ==========================================================================

  BRIDGE_BASE_RATE: {
    id: PRICING_RULE_IDS.BRIDGE_BASE_RATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'bridge_base_interest_rate',
    description: 'Base interest rate for bridge/fix-and-flip loans',
    category: 'interest_rate',
    entityType: 'bridge',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'ltv_ratio',
      tiers: [
        { min: 0, max: 0.65, value: 0.1 },
        { min: 0.65, max: 0.7, value: 0.105 },
        { min: 0.7, max: 0.75, value: 0.11 },
        { min: 0.75, max: 0.8, value: 0.12 },
      ],
      method: 'flat',
    },
    outputField: 'base_rate',
    outputLabel: 'Bridge Base Rate',
    priority: 1,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  BRIDGE_REHAB_BUDGET_FEE: {
    id: PRICING_RULE_IDS.BRIDGE_REHAB_BUDGET_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'bridge_rehab_budget_fee',
    description: 'Fee based on renovation/rehab budget for fix-and-flip loans',
    category: 'fee',
    entityType: 'bridge',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'rehab_budget',
      tiers: [
        { min: 0, max: 50000, percentage: 0.02 },
        { min: 50000, max: 150000, percentage: 0.0175 },
        { min: 150000, max: 500000, percentage: 0.015 },
        { min: 500000, max: Infinity, percentage: 0.0125 },
      ],
      method: 'marginal',
    },
    outputField: 'rehab_holdback_fee',
    outputLabel: 'Rehab Holdback Fee',
    priority: 15,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  BRIDGE_EXIT_STRATEGY_ADJ: {
    id: PRICING_RULE_IDS.BRIDGE_EXIT_STRATEGY_ADJ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'bridge_exit_strategy_adjustment',
    description: 'Rate adjustment based on exit strategy clarity',
    category: 'interest_rate',
    entityType: 'bridge',
    calculationType: 'LOOKUP',
    calculationConfig: {
      lookupKey: 'exit_strategy',
      lookupData: {
        sale_contracted: -0.0025, // Sale already under contract
        sale_listed: 0, // Listed for sale
        refinance_approved: -0.0025, // Refinance pre-approved
        refinance_pending: 0.005, // Refinance application pending
        undetermined: 0.01, // No clear exit strategy
      },
      defaultValue: 0.005,
    },
    outputField: 'exit_strategy_adj',
    outputLabel: 'Exit Strategy Adjustment',
    priority: 5,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  BRIDGE_EXTENSION_FEE: {
    id: PRICING_RULE_IDS.BRIDGE_EXTENSION_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'bridge_extension_fee',
    description: 'Fee for loan extension beyond original term',
    category: 'fee',
    entityType: 'bridge',
    conditions: { is_extension: true },
    calculationType: 'PERCENTAGE',
    calculationConfig: {
      baseField: 'loan_amount',
      percentage: 0.01, // 1% extension fee
      minimum: 1500,
      maximum: 25000,
    },
    outputField: 'extension_fee',
    outputLabel: 'Extension Fee',
    priority: 25,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  // ==========================================================================
  // CONSTRUCTION INVESTMENT TYPE RULES (Issue #526)
  // ==========================================================================

  CONSTRUCTION_BASE_RATE: {
    id: PRICING_RULE_IDS.CONSTRUCTION_BASE_RATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'construction_base_interest_rate',
    description: 'Base interest rate for ground-up construction loans',
    category: 'interest_rate',
    entityType: 'construction',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'ltc_ratio', // Loan-to-Cost for construction
      tiers: [
        { min: 0, max: 0.7, value: 0.115 },
        { min: 0.7, max: 0.8, value: 0.125 },
        { min: 0.8, max: 0.85, value: 0.135 },
        { min: 0.85, max: 0.9, value: 0.145 },
      ],
      method: 'flat',
    },
    outputField: 'base_rate',
    outputLabel: 'Construction Base Rate',
    priority: 1,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  CONSTRUCTION_DRAW_FEE: {
    id: PRICING_RULE_IDS.CONSTRUCTION_DRAW_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'construction_draw_fee',
    description: 'Per-draw administration fee for construction draws',
    category: 'fee',
    entityType: 'construction',
    calculationType: 'FORMULA',
    calculationConfig: {
      formula: 'base_fee + (draw_amount / 100000) * increment',
      variables: {
        base_fee: 150,
        increment: 25,
      },
      minimum: 150,
      maximum: 500,
    },
    outputField: 'draw_fee',
    outputLabel: 'Draw Administration Fee',
    priority: 16,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  CONSTRUCTION_INSPECTION_FEE: {
    id: PRICING_RULE_IDS.CONSTRUCTION_INSPECTION_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'construction_inspection_fee',
    description: 'Per-inspection fee for construction progress inspections',
    category: 'fee',
    entityType: 'construction',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'total_construction_cost',
      tiers: [
        { min: 0, max: 500000, amount: 175 },
        { min: 500000, max: 1500000, amount: 250 },
        { min: 1500000, max: 5000000, amount: 350 },
        { min: 5000000, max: Infinity, amount: 500 },
      ],
      method: 'flat',
    },
    outputField: 'inspection_fee',
    outputLabel: 'Inspection Fee (per inspection)',
    priority: 17,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  CONSTRUCTION_CONTINGENCY_RESERVE: {
    id: PRICING_RULE_IDS.CONSTRUCTION_CONTINGENCY_RESERVE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'construction_contingency_reserve',
    description: 'Required contingency reserve as percentage of construction budget',
    category: 'reserve',
    entityType: 'construction',
    calculationType: 'PERCENTAGE',
    calculationConfig: {
      baseField: 'construction_budget',
      percentage: 0.1, // 10% contingency
      minimum: 15000,
      maximum: 500000,
    },
    outputField: 'contingency_reserve',
    outputLabel: 'Contingency Reserve',
    priority: 18,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  // ==========================================================================
  // RENTAL/DSCR INVESTMENT TYPE RULES (Issue #526)
  // ==========================================================================

  DSCR_BASE_RATE: {
    id: PRICING_RULE_IDS.DSCR_BASE_RATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'dscr_base_interest_rate',
    description: 'Base interest rate for DSCR rental loans',
    category: 'interest_rate',
    entityType: 'rental',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'term_years',
      tiers: [
        { min: 0, max: 5, value: 0.0825 }, // 5-year term
        { min: 5, max: 7, value: 0.085 }, // 7-year term
        { min: 7, max: 10, value: 0.0875 }, // 10-year term
        { min: 10, max: 30, value: 0.0925 }, // 30-year term
      ],
      method: 'flat',
    },
    outputField: 'base_rate',
    outputLabel: 'DSCR Base Rate',
    priority: 1,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  DSCR_RATIO_ADJUSTMENT: {
    id: PRICING_RULE_IDS.DSCR_RATIO_ADJUSTMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'dscr_ratio_adjustment',
    description: 'Rate adjustment based on Debt Service Coverage Ratio',
    category: 'interest_rate',
    entityType: 'rental',
    calculationType: 'TIERED',
    calculationConfig: {
      baseField: 'dscr_ratio',
      tiers: [
        { min: 1.5, max: Infinity, adjustment: -0.005 }, // Strong DSCR
        { min: 1.25, max: 1.5, adjustment: -0.0025 }, // Good DSCR
        { min: 1.1, max: 1.25, adjustment: 0 }, // Acceptable DSCR
        { min: 1.0, max: 1.1, adjustment: 0.0075 }, // Marginal DSCR
        { min: 0.75, max: 1.0, adjustment: 0.015 }, // Below 1.0 (no-ratio program)
      ],
      method: 'flat',
    },
    outputField: 'dscr_adjustment',
    outputLabel: 'DSCR Ratio Adjustment',
    priority: 2,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  DSCR_PREPAY_PENALTY: {
    id: PRICING_RULE_IDS.DSCR_PREPAY_PENALTY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'dscr_prepayment_penalty',
    description: 'Prepayment penalty structure for DSCR loans',
    category: 'fee',
    entityType: 'rental',
    conditions: { has_prepayment_penalty: true },
    calculationType: 'LOOKUP',
    calculationConfig: {
      lookupKey: 'prepay_structure',
      lookupData: {
        '5-4-3-2-1': { description: '5% Y1, 4% Y2, 3% Y3, 2% Y4, 1% Y5', rate_credit: -0.0025 },
        '3-2-1': { description: '3% Y1, 2% Y2, 1% Y3', rate_credit: -0.00125 },
        none: { description: 'No prepayment penalty', rate_credit: 0.005 },
      },
      defaultValue: { description: 'Standard 3-2-1', rate_credit: -0.00125 },
    },
    outputField: 'prepay_penalty',
    outputLabel: 'Prepayment Penalty',
    priority: 6,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  DSCR_CASH_OUT_ADJUSTMENT: {
    id: PRICING_RULE_IDS.DSCR_CASH_OUT_ADJUSTMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'dscr_cash_out_adjustment',
    description: 'Rate adjustment for cash-out refinance on DSCR loans',
    category: 'interest_rate',
    entityType: 'rental',
    conditions: { loan_purpose: 'cash_out' },
    calculationType: 'FLAT',
    calculationConfig: {
      adjustment: 0.0025, // 0.25% rate increase for cash-out
    },
    outputField: 'cash_out_adjustment',
    outputLabel: 'Cash-Out Adjustment',
    priority: 7,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  // ==========================================================================
  // COMMERCIAL INVESTMENT TYPE RULES (Issue #526)
  // ==========================================================================

  COMMERCIAL_BASE_RATE: {
    id: PRICING_RULE_IDS.COMMERCIAL_BASE_RATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'commercial_base_interest_rate',
    description: 'Base interest rate for commercial real estate loans',
    category: 'interest_rate',
    entityType: 'commercial',
    calculationType: 'LOOKUP',
    calculationConfig: {
      lookupKey: 'property_subtype',
      lookupData: {
        office: 0.085,
        retail: 0.09,
        industrial: 0.0825,
        mixed_use: 0.0875,
        hospitality: 0.095,
        self_storage: 0.08,
        multifamily_5_plus: 0.0775,
      },
      defaultValue: 0.09,
    },
    outputField: 'base_rate',
    outputLabel: 'Commercial Base Rate',
    priority: 1,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  COMMERCIAL_TENANT_QUALITY_ADJ: {
    id: PRICING_RULE_IDS.COMMERCIAL_TENANT_QUALITY_ADJ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'commercial_tenant_quality_adjustment',
    description: 'Rate adjustment based on tenant credit quality and lease terms',
    category: 'interest_rate',
    entityType: 'commercial',
    calculationType: 'LOOKUP',
    calculationConfig: {
      lookupKey: 'tenant_quality',
      lookupData: {
        national_credit: -0.005, // National credit tenant (e.g., Walgreens, Starbucks)
        regional_credit: -0.0025, // Regional credit tenant
        local_established: 0, // Established local tenant
        local_new: 0.005, // New local tenant
        vacant: 0.01, // Vacant property
      },
      defaultValue: 0,
    },
    outputField: 'tenant_quality_adj',
    outputLabel: 'Tenant Quality Adjustment',
    priority: 3,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  COMMERCIAL_ENVIRONMENTAL_FEE: {
    id: PRICING_RULE_IDS.COMMERCIAL_ENVIRONMENTAL_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'commercial_environmental_fee',
    description: 'Environmental assessment fee based on property type and risk',
    category: 'fee',
    entityType: 'commercial',
    calculationType: 'LOOKUP',
    calculationConfig: {
      lookupKey: 'environmental_risk',
      lookupData: {
        low_risk_phase1: 1500, // Standard Phase I ESA
        moderate_risk_phase1: 2500, // Enhanced Phase I
        high_risk_phase2: 8500, // Phase II required
        industrial_phase2: 15000, // Industrial with Phase II
      },
      defaultValue: 2500,
    },
    outputField: 'environmental_fee',
    outputLabel: 'Environmental Assessment Fee',
    priority: 19,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  COMMERCIAL_LEASE_REVIEW_FEE: {
    id: PRICING_RULE_IDS.COMMERCIAL_LEASE_REVIEW_FEE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'commercial_lease_review_fee',
    description: 'Fee for reviewing commercial lease agreements',
    category: 'fee',
    entityType: 'commercial',
    calculationType: 'FORMULA',
    calculationConfig: {
      formula: 'base_fee + (tenant_count * per_tenant_fee)',
      variables: {
        base_fee: 500,
        per_tenant_fee: 150,
      },
      minimum: 500,
      maximum: 5000,
    },
    outputField: 'lease_review_fee',
    outputLabel: 'Lease Review Fee',
    priority: 20,
    effectiveDate: '2025-01-01',
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// PRICING LOOKUP DATA
// =============================================================================

export const PRICING_LOOKUPS: Record<keyof typeof PRICING_LOOKUP_IDS, PricingLookupSeedData> = {
  STATE_FEES: {
    id: PRICING_LOOKUP_IDS.STATE_FEES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'state_fees',
    description: 'State-specific recording and documentation fees',
    lookupData: {
      TX: 150,
      FL: 175,
      CA: 225,
      AZ: 125,
      GA: 100,
      NY: 350,
      NJ: 275,
      PA: 200,
      CO: 150,
      WA: 175,
    },
    version: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  PROPERTY_TYPE_MULTIPLIERS: {
    id: PRICING_LOOKUP_IDS.PROPERTY_TYPE_MULTIPLIERS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'property_type_multipliers',
    description: 'Rate adjustment by property type',
    lookupData: {
      sfr: 0,
      condo: 0.0025,
      townhouse: 0.001,
      multi_family_2_4: 0.005,
      multi_family_5_plus: 0.0075,
      commercial: 0.015,
      mixed_use: 0.01,
      industrial: 0.02,
      land: 0.025,
    },
    version: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  LOAN_PRODUCT_BASE_RATES: {
    id: PRICING_LOOKUP_IDS.LOAN_PRODUCT_BASE_RATES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'loan_product_base_rates',
    description: 'Base interest rates by loan product type',
    lookupData: {
      bridge: 0.1025,
      fix_flip: 0.11,
      dscr: 0.085,
      construction: 0.12,
      ground_up: 0.125,
      hard_money: 0.14,
      commercial: 0.09,
    },
    version: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  DOCUMENT_FEES: {
    id: PRICING_LOOKUP_IDS.DOCUMENT_FEES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'document_fees',
    description: 'Per-document processing fees',
    lookupData: {
      title_search: 250,
      title_insurance: 750,
      flood_cert: 35,
      credit_report: 50,
      tax_service: 85,
      environmental: 1500,
      survey: 450,
    },
    version: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// PRICING MATRIX DATA
// =============================================================================

export const PRICING_MATRICES: Record<keyof typeof PRICING_MATRIX_IDS, PricingMatrixSeedData> = {
  INTEREST_RATE_MATRIX: {
    id: PRICING_MATRIX_IDS.INTEREST_RATE_MATRIX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'interest_rate_matrix',
    description: 'Interest rate matrix based on credit score and LTV',
    dimensions: [
      {
        name: 'credit_score',
        type: 'range',
        ranges: [
          { min: 760, max: 850, label: 'excellent' },
          { min: 720, max: 759, label: 'good' },
          { min: 680, max: 719, label: 'fair' },
          { min: 640, max: 679, label: 'poor' },
          { min: 0, max: 639, label: 'bad' },
        ],
      },
      {
        name: 'ltv',
        type: 'range',
        ranges: [
          { min: 0, max: 0.6, label: 'low' },
          { min: 0.6, max: 0.7, label: 'medium' },
          { min: 0.7, max: 0.8, label: 'high' },
          { min: 0.8, max: 1.0, label: 'very_high' },
        ],
      },
    ],
    matrixData: {
      // excellent credit
      excellent_low: 0.085,
      excellent_medium: 0.09,
      excellent_high: 0.095,
      excellent_very_high: 0.105,
      // good credit
      good_low: 0.09,
      good_medium: 0.095,
      good_high: 0.1,
      good_very_high: 0.11,
      // fair credit
      fair_low: 0.095,
      fair_medium: 0.1,
      fair_high: 0.105,
      fair_very_high: 0.115,
      // poor credit
      poor_low: 0.105,
      poor_medium: 0.11,
      poor_high: 0.115,
      poor_very_high: 0.125,
      // bad credit
      bad_low: 0.12,
      bad_medium: 0.125,
      bad_high: 0.13,
      bad_very_high: 0.14,
    },
    version: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  ORIGINATION_FEE_MATRIX: {
    id: PRICING_MATRIX_IDS.ORIGINATION_FEE_MATRIX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'origination_fee_matrix',
    description: 'Origination fee percentage by loan amount and term',
    dimensions: [
      {
        name: 'loan_amount',
        type: 'range',
        ranges: [
          { min: 0, max: 500000, label: 'small' },
          { min: 500000, max: 1500000, label: 'medium' },
          { min: 1500000, max: 5000000, label: 'large' },
          { min: 5000000, max: Infinity, label: 'jumbo' },
        ],
      },
      {
        name: 'term_months',
        type: 'discrete',
        values: ['6', '12', '18', '24', '36'],
      },
    ],
    matrixData: {
      // small loans
      small_6: 0.025,
      small_12: 0.02,
      small_18: 0.0175,
      small_24: 0.015,
      small_36: 0.0125,
      // medium loans
      medium_6: 0.02,
      medium_12: 0.0175,
      medium_18: 0.015,
      medium_24: 0.0125,
      medium_36: 0.01,
      // large loans
      large_6: 0.0175,
      large_12: 0.015,
      large_18: 0.0125,
      large_24: 0.01,
      large_36: 0.0075,
      // jumbo loans
      jumbo_6: 0.015,
      jumbo_12: 0.0125,
      jumbo_18: 0.01,
      jumbo_24: 0.0075,
      jumbo_36: 0.005,
    },
    version: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  RISK_ADJUSTMENT_MATRIX: {
    id: PRICING_MATRIX_IDS.RISK_ADJUSTMENT_MATRIX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'risk_adjustment_matrix',
    description: 'Risk-based rate adjustment by experience and property condition',
    dimensions: [
      {
        name: 'borrower_experience',
        type: 'discrete',
        values: ['first_time', 'experienced', 'professional'],
      },
      {
        name: 'property_condition',
        type: 'discrete',
        values: ['excellent', 'good', 'fair', 'poor'],
      },
    ],
    matrixData: {
      // first time borrowers
      first_time_excellent: 0.01,
      first_time_good: 0.015,
      first_time_fair: 0.025,
      first_time_poor: 0.04,
      // experienced borrowers
      experienced_excellent: 0.005,
      experienced_good: 0.0075,
      experienced_fair: 0.015,
      experienced_poor: 0.025,
      // professional borrowers
      professional_excellent: 0,
      professional_good: 0.0025,
      professional_fair: 0.0075,
      professional_poor: 0.015,
    },
    version: 1,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// PRICING QUOTE DATA (Sample quotes)
// =============================================================================

export const PRICING_QUOTES: Record<keyof typeof PRICING_QUOTE_IDS, PricingQuoteSeedData> = {
  QUOTE_MARCUS_BRIDGE: {
    id: PRICING_QUOTE_IDS.QUOTE_MARCUS_BRIDGE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'loan',
    inputData: {
      loan_type: 'bridge',
      loan_amount: 2500000,
      property_value: 3500000,
      ltv_ratio: 0.714,
      credit_score: 740,
      property_type: 'commercial',
      term_months: 12,
      state: 'TX',
      borrower_name: 'Marcus Thompson',
    },
    totalAmount: 62445,
    currency: 'USD',
    status: 'LOCKED',
    expiresAt: '2026-02-15',
    notes: 'Bridge loan for commercial acquisition - rate locked for 30 days',
    metadata: {
      calculations: {
        base_rate: 0.1025,
        ltv_adjustment: 0.005,
        credit_adjustment: -0.005,
        property_adjustment: 0.015,
        final_rate: 0.1175,
        origination_fee: 50000,
        processing_fee: 995,
        appraisal_fee: 1250,
        underwriting_fee: 2000,
        state_fees: 150,
        document_fees: 8050,
      },
    },
    createdBy: USER_IDS.MIKE_CHEN,
  },

  QUOTE_JENNIFER_FIXFLIP: {
    id: PRICING_QUOTE_IDS.QUOTE_JENNIFER_FIXFLIP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'loan',
    inputData: {
      loan_type: 'fix_flip',
      loan_amount: 1800000,
      property_value: 2400000,
      ltv_ratio: 0.75,
      credit_score: 710,
      property_type: 'sfr',
      term_months: 9,
      state: 'FL',
      borrower_name: 'Jennifer Walsh',
    },
    totalAmount: 45670,
    currency: 'USD',
    status: 'DRAFT',
    expiresAt: '2026-02-01',
    notes: 'Fix and flip loan - awaiting appraisal',
    createdBy: USER_IDS.SARAH_WILLIAMS,
  },

  QUOTE_COMMERCIAL_DSCR: {
    id: PRICING_QUOTE_IDS.QUOTE_COMMERCIAL_DSCR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'loan',
    inputData: {
      loan_type: 'dscr',
      loan_amount: 4500000,
      property_value: 6000000,
      ltv_ratio: 0.75,
      credit_score: 760,
      property_type: 'multi_family_5_plus',
      term_months: 24,
      state: 'CA',
      dscr_ratio: 1.35,
      noi: 450000,
    },
    totalAmount: 78500,
    currency: 'USD',
    status: 'ACCEPTED',
    expiresAt: '2026-01-30',
    notes: 'DSCR loan accepted - proceeding to closing',
    createdBy: USER_IDS.DAVID_PARK,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllPricingRules(workspace?: string): PricingRuleSeedData[] {
  const rules = Object.values(PRICING_RULES);
  if (!workspace) return rules;
  return rules.map((r) => ({ ...r, name: `${r.name}_${workspace}` }));
}

export function getAllPricingLookups(workspace?: string): PricingLookupSeedData[] {
  const lookups = Object.values(PRICING_LOOKUPS);
  if (!workspace) return lookups;
  return lookups.map((l) => ({ ...l, name: `${l.name}_${workspace}` }));
}

export function getAllPricingMatrices(workspace?: string): PricingMatrixSeedData[] {
  const matrices = Object.values(PRICING_MATRICES);
  if (!workspace) return matrices;
  return matrices.map((m) => ({ ...m, name: `${m.name}_${workspace}` }));
}

export function getAllPricingQuotes(): PricingQuoteSeedData[] {
  return Object.values(PRICING_QUOTES);
}

export function getPricingRulesByCategory(category: string): PricingRuleSeedData[] {
  return Object.values(PRICING_RULES).filter((r) => r.category === category);
}

export function getActivePricingRules(): PricingRuleSeedData[] {
  return Object.values(PRICING_RULES).filter((r) => r.isActive);
}

export function getLookupByName(name: string): PricingLookupSeedData | undefined {
  return Object.values(PRICING_LOOKUPS).find((l) => l.name === name);
}

export function getMatrixByName(name: string): PricingMatrixSeedData | undefined {
  return Object.values(PRICING_MATRICES).find((m) => m.name === name);
}
